var _excluded = ["mprGqlUrl", "mprApiUrl", "mprTileUrl"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import MapprComponent from '../components/Mappr';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from '../store';
import { appInit, appSetLanguage } from '../store/actions/appSlice';
import { login, loginDone, logout } from '../store/actions/authSlice';
import ApolloClient from '../apollo';
import useAnalytics from '../hooks/useAnalytics';

/**
 * This is the main component needed to initiate a Mappr application.
 * All components from the library should be nested under a single instance of this component.
 *  */
function MapprWrapper(_ref) {
  var _ref$mprGqlUrl = _ref.mprGqlUrl,
    mprGqlUrl = _ref$mprGqlUrl === void 0 ? 'https://webapi.mappr.xyz/graphql' : _ref$mprGqlUrl,
    _ref$mprApiUrl = _ref.mprApiUrl,
    mprApiUrl = _ref$mprApiUrl === void 0 ? 'https://webapi.mappr.xyz/graphql' : _ref$mprApiUrl,
    _ref$mprTileUrl = _ref.mprTileUrl,
    mprTileUrl = _ref$mprTileUrl === void 0 ? 'https://webapi.mappr.xyz/api' : _ref$mprTileUrl,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(MapprContainer, _extends({}, restProps, {
    mprApiUrl: mprApiUrl,
    mprGqlUrl: mprGqlUrl,
    mprTileUrl: mprTileUrl
  })));
}
function MapprContainer(props) {
  var dispatch = useDispatch();
  var appState = useSelector(function (state) {
    return state.app;
  });
  var authState = useSelector(function (state) {
    return state.auth;
  });
  var theUser = authState.theUser;
  var project = appState.project,
    basemaps = appState.basemaps,
    secondaryLayers = appState.secondaryLayers,
    appLoading = appState.loading,
    map = appState.map;
  useEffect(function () {
    // get the authentication token from local storage if it exists
    var urlParams = new URLSearchParams(window.location.search);
    var token = urlParams.get('token');
    if (props.tokenFn) {
      token = props.tokenFn();
    } else if (props.token) {
      token = props.token;
    }
    ApolloClient.init({
      url: props.mprGqlUrl,
      domain: props.name,
      token: token
    });
    dispatch(appInit({
      domain: props.name,
      language: props.language,
      mprApiUrl: props.mprApiUrl,
      mprTileUrl: props.mprTileUrl,
      layers: props.secondaryLayers
    }));
    return function () {
      var _ApolloClient$client;
      (_ApolloClient$client = ApolloClient.client) === null || _ApolloClient$client === void 0 ? void 0 : _ApolloClient$client.stop();
    };
  }, []);
  var tokenFnVal = props.tokenFn && typeof props.tokenFn == 'function' && props.tokenFn();
  useEffect(function () {
    props.tokenFn && ApolloClient.updateToken(tokenFnVal);
  }, [tokenFnVal]);
  useEffect(function () {
    if (!project || appLoading) {
      return;
    }
    var authSession = localStorage.getItem("mpr." + project.id + ".auth");
    if (authSession) {
      authSession = JSON.parse(authSession);

      // const expirationDate = authSession.expiresAt;
      // const currentDate = new Date();
      // if (Date.parse(currentDate) < Date.parse(expirationDate)) {
      dispatch(loginDone(_extends({}, authSession, {
        projectId: project.id
      })));
      // } else {
      //   this.props.onLogout({ userId: authSession.user.id, projectId });
      // }
    }
  }, [project, appLoading]);
  useEffect(function () {
    dispatch(appSetLanguage({
      language: props.language
    }));
  }, [props.language]);
  useAnalytics();
  return /*#__PURE__*/React.createElement(MapprComponent, _extends({}, props, {
    language: appState.language,
    error: appState.error,
    authError: authState.error,
    project: project,
    basemaps: basemaps,
    draw: map === null || map === void 0 ? void 0 : map.draw,
    secondaryLayers: secondaryLayers,
    loading: appLoading,
    theUser: theUser,
    onLogin: function onLogin(_ref2) {
      var username = _ref2.username,
        password = _ref2.password;
      return dispatch(login({
        username: username,
        password: password
      }));
    },
    onLogout: function onLogout() {
      return dispatch(logout({
        userId: theUser && theUser.id
      }));
    },
    onSetLanguage: function onSetLanguage(language) {
      return dispatch(appSetLanguage({
        language: language
      }));
    }
  }));
}
MapprWrapper.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The project unique domain name */
  name: PropTypes.string.isRequired,
  /** The main url of Mappr GraphQL API */
  mprGqlUrl: PropTypes.string.isRequired,
  /** The main url of Mappr Rest API */
  mprApiUrl: PropTypes.string.isRequired,
  /** The main url of Mappr Rest API */
  mprTileUrl: PropTypes.string.isRequired,
  /** The current application language to be applied on data fetching */
  language: PropTypes.string.isRequired,
  /** The SSO authentication token to use for request authorization */
  token: PropTypes.string,
  /**
   * The SSO authentication token function to use for dynamic, 'on the fly' request authorization (otherwise use 'token')
   * @function
   * @returns {string}
   */
  tokenFn: PropTypes.func,
  /**
   * The list of application secondary layers grouped by sections. Each element of this array is a single group of layers.
   *
   * **Example:**
   * <pre>
   *  [
   *    {
   *      id: 'sect_1',
   *      secondaryLayers: [
   *           {
   *             canBeToggled: true,
   *             'default': null,
   *             disabled: true,
   *             layers: [
   *               {
   *                 filter: null,
   *                 id: 'layer4',
   *                 layout: null,
   *                 maxzoom: null,
   *                 minzoom: 12,
   *                 paint: {
   *                   'line-width': 2.5,
   *                   'line-opacity': 1,
   *                   'line-color': ''#4dcb82'
   *                 }
   *                 source: 'layer4',
   *                 sourceLayer: 'traffic',
   *                 type: 'line'
   *               }
   *             ],
   *             name: 'layer4',
   *             sources: {
   *               layer4: {
   *                 type: 'vector',
   *                 url: 'mapbox://mapbox.mapbox-traffic-v1'
   *               }
   *             },
   *             activeLayers: {
   *               layer4: 'properties.class'
   *             },
   *             customPopUp: false,
   *             popUpTitleField: null,
   *             popUpFields: null
   *           }
   *    }
   * ]
   * </pre>
   *
   * For the `paint`, `type`, `layout` and `source` properties of the layer please refer to the official Mapbox documentation at  https://docs.mapbox.com/mapbox-gl-js/style-spec/
   *  */
  secondaryLayers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    secondaryLayers: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      canBeToggled: PropTypes.bool,
      "default": PropTypes.bool,
      disabled: PropTypes.bool,
      layers: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string.isRequired,
        filter: PropTypes.object,
        id: PropTypes.string.isRequired,
        layout: PropTypes.object,
        maxzoom: PropTypes.number,
        minzoom: PropTypes.number,
        paint: PropTypes.object,
        source: PropTypes.string,
        sourceLayer: PropTypes.string
      })).isRequired,
      /** The data sources of the layer  */
      sources: PropTypes.object.isRequired,
      activeLayers: PropTypes.objectOf(PropTypes.string).isRequired,
      customPopUp: PropTypes.bool,
      popUpTitleField: PropTypes.string,
      // popUpFields: PropTypes.array,
      popupEvent: PropTypes.string
    }))
  })),
  /**
   * A render function that is used to display a UI based on the provided data and methods.  Either the single function as a child this prop should be provided
   * @function
   * @returns {void}
   * @param {Object} props
   * @param {boolean} props.loading  A loading state identifier
   * @param {Object} props.error An error object if any
   * @param {Object} props.authError An authentication error object if any
   * @param {boolean} props.basemaps The list of available basemaps
   * @param {Array} props.secondaryLayers The list of available secondary layers
   * @param {Object} props.project The project configuration object
   * @param {boolean} props.authenticated The auth state for the current end user
   * @param {string} props.language The currently set language
   * @param {Object} methods
   * @param {function} methods.login A method to call when logging in
   * @param {function} methods.logout A method to call when logging out
   * @param {function} methods.setLanguage A method to call when changing the app language
   *  */
  render: PropTypes.func
} : {};
MapprContainer.propTypes = process.env.NODE_ENV !== "production" ? _extends({}, MapprWrapper.propTypes) : {};
export { MapprWrapper as default };
export var Mappr = MapprWrapper;